import { molecule, useMolecule } from 'bunshi/react';
import { useAtom } from 'jotai';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback } from 'react';
import { BookkeepingAsideState } from '~/components/layout/BookkeepingAside';
import { SectionScope } from '~/state/sectionScope';
import { TransactionMetadataFilter } from '~/types/transaction';

export const MIN_REGISTRATION_ASIDE_WIDTH = 1040;
export const MAX_REGISTRATION_ASIDE_WIDTH = 1500;

const ASIDE_ANIMATION_TIME = 200;

const bulkRegistrationAsideMolecule = molecule((_mol, scope) => {
  scope(SectionScope);

  const asideStateAtom = atom<BookkeepingAsideState>(BookkeepingAsideState.UNMOUNTED);
  const transactionsFiltersAtom = atom<TransactionMetadataFilter | null>(null);

  const openAsideAtom = atom(null, (get, set, filter: TransactionMetadataFilter) => {
    set(transactionsFiltersAtom, filter);

    const currentValue = get(asideStateAtom);
    if (currentValue !== BookkeepingAsideState.OPEN) {
      set(asideStateAtom, BookkeepingAsideState.CLOSED);
      setTimeout(() => {
        set(asideStateAtom, BookkeepingAsideState.OPEN);
      });
    }
  });

  const closeAsideAtom = atom(null, (get, set, onClose?: () => void) => {
    const currentValue = get(asideStateAtom);
    set(transactionsFiltersAtom, null);

    if (currentValue === BookkeepingAsideState.CLOSED) {
      set(asideStateAtom, BookkeepingAsideState.UNMOUNTED);
      onClose?.();
    } else {
      set(asideStateAtom, BookkeepingAsideState.CLOSED);
      window.setTimeout(() => {
        set(asideStateAtom, BookkeepingAsideState.UNMOUNTED);
        onClose?.();
      }, ASIDE_ANIMATION_TIME);
    }
  });

  return {
    asideStateAtom,
    transactionsFiltersAtom,
    openAsideAtom,
    closeAsideAtom,
  };
});

export const useBulkRegistrationAsideState = () => {
  const { asideStateAtom } = useMolecule(bulkRegistrationAsideMolecule);
  return useAtomValue(asideStateAtom);
};
export const useOpenBulkRegistrationAside = () => {
  const { openAsideAtom } = useMolecule(bulkRegistrationAsideMolecule);
  return useSetAtom(openAsideAtom);
};

export const useTransactionsFiltersForBulk = () => {
  const { transactionsFiltersAtom } = useMolecule(bulkRegistrationAsideMolecule);
  return useAtomValue(transactionsFiltersAtom);
};

export const useCloseBulkRegistrationAside = () => {
  const { closeAsideAtom } = useMolecule(bulkRegistrationAsideMolecule);
  const closeAside = useSetAtom(closeAsideAtom);
  return useCallback(
    (onClose?: () => void) => {
      closeAside(onClose);
    },
    [closeAside],
  );
};

const asideWidthAtom = atomWithStorage<number>('registration-aside-width', MIN_REGISTRATION_ASIDE_WIDTH, undefined, {
  getOnInit: true,
});

export const useAsideWidth = () => useAtom(asideWidthAtom);
